import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Crown, X, Download, Share2 } from "lucide-react";
import { money } from "@/lib/format";

export default function RankCelebration({ rankInfo, userName, onClose }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const end = Date.now() + 1600;
    const colors = ["#F59E0B", "#D4AF37", "#FDE68A", "#ffffff"];
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0 }, colors });
      confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    confetti({ particleCount: 120, spread: 90, origin: { y: 0.4 }, colors });
  }, []);

  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#050507" });
      const link = document.createElement("a");
      link.download = `moneymoonx-${rankInfo.name.replace(/\s+/g, "-").toLowerCase()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Badge downloaded");
    } catch { toast.error("Download failed"); }
    setBusy(false);
  };

  const share = async () => {
    const text = `🏆 I just achieved ${rankInfo.name} on MoneyMoonX! Weekly salary reward: ${money(rankInfo.salary, 0)}. Building wealth, securing futures. 🚀`;
    try {
      if (navigator.share) await navigator.share({ title: "MoneyMoonX Rank Achieved", text });
      else { await navigator.clipboard.writeText(text); toast.success("Achievement copied to clipboard"); }
    } catch { /* cancelled */ }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/80 backdrop-blur-sm" data-testid="rank-celebration">
      <div className="w-full max-w-md relative">
        <button data-testid="celebration-close" onClick={onClose} className="absolute -top-2 -right-2 z-10 bg-[#14151C] border border-amber-500/30 rounded-full p-2 text-slate-400 hover:text-amber-400"><X className="w-4 h-4" /></button>

        {/* shareable badge card */}
        <div ref={cardRef} className="rounded-3xl overflow-hidden border-2 border-amber-400 bg-gradient-to-b from-[#1a1608] to-[#0a0a0d] p-8 text-center relative">
          <div className="gold-glow top-0 left-1/2 -translate-x-1/2" />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.3em] text-amber-400 mb-6">MoneyMoonX · Rank Achieved</div>
            <div className="w-24 h-24 mx-auto rounded-full grid place-items-center mb-4" style={{ background: "rgba(245,158,11,0.15)", border: "3px solid #F59E0B", boxShadow: "0 0 40px rgba(245,158,11,0.4)" }}>
              <Crown className="w-12 h-12 text-amber-400" />
            </div>
            <div className="font-display text-3xl font-extrabold gold-text">{rankInfo.name}</div>
            <div className="text-slate-300 mt-2">Congratulations, {userName?.split(" ")[0]}!</div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-black/40 border border-amber-500/20 p-3">
                <div className="text-[10px] text-slate-500 uppercase">Matched</div>
                <div className="font-mono-fin font-bold text-amber-300">{money(rankInfo.matched, 0)}</div>
              </div>
              <div className="rounded-xl bg-black/40 border border-amber-500/20 p-3">
                <div className="text-[10px] text-slate-500 uppercase">Weekly Salary</div>
                <div className="font-mono-fin font-bold text-emerald-400">{money(rankInfo.salary, 0)}</div>
              </div>
            </div>
            <div className="text-xs text-amber-500/70 mt-6 tracking-wide">SMART CHOICE. BRIGHTER FUTURE.</div>
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button data-testid="celebration-download" onClick={download} disabled={busy} className="flex-1 border border-amber-500/30 hover:bg-amber-500/10 rounded-xl py-3 font-bold text-amber-300 flex items-center justify-center gap-2 transition-all disabled:opacity-60">
            <Download className="w-4 h-4" /> {busy ? "Saving..." : "Download"}
          </button>
          <button data-testid="celebration-share" onClick={share} className="flex-1 gold-btn rounded-xl py-3 font-bold flex items-center justify-center gap-2">
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>
      </div>
    </div>
  );
}
